import { Box, Grid, styled, TextField, Typography, useTheme } from "@mui/material"
import { TagType } from "../services/api/tag"

import { ReactComponent as SnowPatchSVG } from "../assets/svg/snow-patch.svg"
import { ReactComponent as BatSVG } from "../assets/svg/bat.svg"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

import LogoHy from "../assets/png/hyris_hy.png"
import LogoRis from "../assets/png/hyris_ris.png"
import { StylelessLink } from "./Util"

interface PageTitleProps {
  children: React.ReactNode
}

export const PageTitle = ({ children }: PageTitleProps) => {
  return (
    <Typography component="h1" variant="h1" align="center" fontSize={"3rem"} pt={4} sx={{ userSelect: "none" }}>
      {children}
    </Typography>
  )
}

export const FieldContainer = styled(Grid)({
  padding: "20px",
})

export const StyledTextField = styled(TextField)({
  width: "100%",
})

interface PageTitleProps {
  children: React.ReactNode
  zIndex?: number
}

const snowyPages = ["/"]

export const Main = ({ children }: PageTitleProps) => {
  const theme = useTheme()
  const location = useLocation()
  return (
    <Box
      component="main"
      sx={{
        position: "relative",
        backgroundColor: theme.palette.element.main.background,
        color: theme.palette.element.main.text,
        minHeight: "200px",
        background: theme.name === "classic" ? "radial-gradient(circle, #282828 0%, #080808 60%)" : undefined,
      }}
    >
      {children}
      {theme.name === "christmas" && (
        <>
          {
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "100px",
                zIndex: 1,
                overflow: "hidden",
                pointerEvents: "none",
              }}
            >
              {[...Array(20)].map((k) => (
                <SnowPatch key={k} />
              ))}
            </Box>
          }
          {snowyPages.some((path) => location.pathname === path) && <SnowBox n={50} baseOpacity={0.7} />}
        </>
      )}
    </Box>
  )
}

export const SnowPatch = (): React.ReactElement => {
  const [width] = useState(100 + Math.random() * 250)
  const [color] = useState(70 + Math.random() * 30)
  const [x] = useState(Math.random() * 140 - 20)
  return (
    <SnowPatchSVG
      style={{
        position: "absolute",
        bottom: "-1px",
        left: `${x}%`,
        width: `${width}px`,
        height: `auto`,
        zIndex: 1,
        fill: `hsl(210, 0%, ${color}%)`,
      }}
    />
  )
}

export const FillingBox = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
})

export const SnowFlake = ({ baseOpacity = 1 }: { baseOpacity?: number }): React.ReactElement => {
  const [size] = useState(Math.random() * 3 + 2)
  const [positionX] = useState(Math.random() * 100)
  const fallDuration = 15 / size
  const [delay] = useState(Math.random() * fallDuration)

  return (
    <Box
      className="snowflake"
      sx={{
        opacity: baseOpacity,
        position: "absolute",
        top: "-10px",
        left: `${positionX}%`,
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        backgroundColor: "white",
        transition: "opacity 0.3s linear",
        animation: `fall ${fallDuration}s linear infinite`,
        animationDelay: `${delay}s`,
        "@keyframes fall": {
          "0%": {
            transform: "translate(0px, 0px)",
          },
          "80%": {
            backgroundColor: "white",
          },
          "100%": {
            transform: "translate(20px, 170px)",
            backgroundColor: "transparent",
          },
        },
      }}
    />
  )
}

export const SnowBox = ({ n, baseOpacity }: { n: number; baseOpacity?: number }): React.ReactElement => {
  return (
    <FillingBox sx={{ zIndex: 2, pointerEvents: "none", overflow: "hidden" }}>
      {[...Array(n)].map((k) => (
        <SnowFlake key={k} baseOpacity={baseOpacity} />
      ))}
    </FillingBox>
  )
}

export const Bat = ({ defaultFilghtTime = 1 }: { defaultFilghtTime?: number }): React.ReactElement => {
  const [size] = useState(30 + Math.random() * 50)
  const [positionY] = useState(Math.random() * 100)
  const [flightDuration] = useState(defaultFilghtTime * (1 + Math.random() / 2))
  const [dy] = useState([Math.random() * 50 - 25, Math.random() * 50 - 25, Math.random() * 50 - 25])
  const [delay] = useState(Math.random() * 0.5)

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        "@keyframes flight": {
          "0%": {
            left: "-50%",
            transform: `translateY(0%)`,
          },
          "25%": {
            transform: `translateY(${dy[0]}px)`,
          },
          "50%": {
            transform: `translateY(${dy[1]}px)`,
          },
          "75%": {
            transform: `translateY(${dy[2]}px)`,
          },
          "100%": {
            left: "150%",
            transform: `translateY(0%)`,
          },
        },
      }}
    >
      <BatSVG
        style={{
          height: `${size}px`,
          width: "auto",
          position: "absolute",
          top: `calc(${positionY}% - ${size / 2}px)`,
          animation: `flight ${flightDuration}s linear`,
          animationDelay: `${delay}s`,
          animationFillMode: "both",
          filter: "drop-shadow(0 0 5px black)",
        }}
      />
    </Box>
  )
}

export interface BatBoxHandle {
  spawn: () => void
}

export interface BatBoxProps {
  defaultFilghtTime?: number
}

export const BatBox = forwardRef<BatBoxHandle, BatBoxProps>(({ defaultFilghtTime = 1 }, ref): React.ReactElement => {
  const [bats, setBats] = useState<number[]>([])
  const counter = useRef(0)

  useImperativeHandle(ref, () => ({
    spawn: () => {
      const id = counter.current++
      setBats((prev) => [...prev, id])
      setTimeout(() => setBats((prev) => prev.filter((b) => b !== id)), defaultFilghtTime * 2000 + 500)
    },
  }))

  return (
    <FillingBox sx={{ zIndex: 2, pointerEvents: "none", overflow: "hidden" }}>
      {bats.map((id) => (
        <Bat key={id} defaultFilghtTime={defaultFilghtTime} />
      ))}
    </FillingBox>
  )
})

interface TagChipProps {
  tag: TagType
}

export const TagChip = ({ tag }: TagChipProps) => {
  return (
    <StylelessLink to={`/videos?tag=${tag.id}`}>
      <Box
        display="inline-block"
        sx={{
          backgroundColor: "#2C579F",
          borderRadius: "3px",
          color: "white",
          fontSize: "12px",
          padding: "2px 4px",
          cursor: "pointer",
        }}
        maxHeight={"24px"}
        alignContent={"center"}
        justifyContent={"center"}
      >
        {tag.name}
      </Box>
    </StylelessLink>
  )
}

export const HoverOverlay = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",

  "&:hover .overlay": {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },

  ".obscured.overlay": {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },

  ".adaptiveBorderRadius": {
    borderRadius: theme.name === "classic" ? undefined : "10px",
    transition: ".3s",
  },

  "&:hover .adaptiveBorderRadius": {
    borderRadius: theme.name === "classic" ? undefined : "0px",
  },

  ".playlistSpreadTop": {
    transform: "translate(1%, 1%)",
  },

  "&:hover .playlistSpreadTop": {
    transform: "translate(0%, 0%)",
  },

  ".playlistSpreadMiddle": {
    transform: "translate(4%, 4%)",
  },

  "&:hover .playlistSpreadMiddle": {
    transform: "translate(5%, 5%)",
  },

  ".playlistSpreadBottom": {
    transform: "translate(6%, 6%)",
  },

  "&:hover .playlistSpreadBottom": {
    transform: "translate(10%, 10%)",
  },
}))

export const Overlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "transparent",
  transition: "background-color 0.3s ease",
  zIndex: 1,
})

export const RetroHyrisButton = ({ onClick }: { onClick?: () => void }): React.ReactElement => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        position: "fixed",
        top: "-20px",
        left: "-20px",
        height: "160px",
        width: "160px",
        zIndex: 1500,
        backgroundColor: "black",
        borderRadius: "500px",
        boxShadow: "0px 0px 10px 10px black",
        transition: ".3s",
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          height: "180px",
          width: "180px",
        },
        "& .hy": {
          height: "110px",
          transition: ".3s",
        },
        "& .ris": {
          height: "30px",
          marginLeft: "-10px",
          transition: ".3s",
        },
        "&:hover .hy": {
          height: "132px",
          transform: "rotate(180deg)",
        },
        "&:hover .ris": {
          height: "36px",
          marginLeft: "-12px",
        },
      }}
      onClick={onClick}
    >
      <img src={LogoHy} className="hy" />
      <img src={LogoRis} className="ris" />
    </Box>
  )
}
